import { DOCUMENT, NgClass } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
  Component,
  ComponentRef,
  computed,
  ElementRef,
  EventEmitter,
  forwardRef,
  inject,
  Injector,
  Input,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  signal,
  Type,
  ViewChild,
  ViewContainerRef,
  ViewEncapsulation,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ApizUIProcess, ProcessRequest } from '@compiere-ws/models/apiz-domains';
import { DataStore } from '@compiere-ws/models/compiere-data-json';
import { CompiereProcessService } from '@compiere-ws/services/compiere-process/compiere-process.service';
import { ProcessInProgressService } from '@compiere-ws/services/process-in-progress/process-in-progress.service';
import { SocketService } from '@compiere-ws/services/socket/socket.service';
import ProcessLogsUiComponent from '@iupics-components/message/process-logs-ui/process-logs-ui.component';
import { IAutocomplete, PrintFormatAutocomplete } from '@iupics-components/models/autocomplete-interfaces';
import {
  FormModalDisplayRequester,
  isFormModalDisplayRequest,
  ModalDisplayRequest,
} from '@iupics-components/models/ui-request';
import AutocompleteUiComponent from '@iupics-components/standard/fields/autocomplete-ui/autocomplete-ui.component';
import CalendarUiComponent from '@iupics-components/standard/fields/calendar-ui/calendar-ui.component';
import InputFileUiComponent from '@iupics-components/standard/fields/input-file-ui/input-file-ui.component';
import EditViewUiComponent from '@iupics-components/standard/layouts/edit-view-ui/edit-view-ui.component';
import { EditViewUtils } from '@iupics-components/standard/layouts/edit-view-ui/utils/edit-view.utils';
import ModalUiComponent from '@iupics-components/standard/layouts/modal-ui/modal-ui.component';
import { CacheManagerService } from '@iupics-manager/managers/cache-manager/cache-manager.service';
import { DataStoreService } from '@iupics-manager/managers/data-store/data-store.service';
import { MessageManagerService } from '@iupics-manager/managers/message/message-manager.service';
import { SecurityManagerService } from '@iupics-manager/managers/security-manager/security-manager.service';
import { UICreatorService } from '@iupics-manager/managers/ui-creator/ui-creator.service';
import { WindowFactoryService } from '@iupics-manager/managers/ui-creator/window-factory/window-factory.service';
import { AbstractDataContainer } from '@iupics-manager/models/abstract-datacontainer';
import { AbstractDynamicComponent } from '@iupics-manager/models/abstract-dynamic-component';
import { DynamicComponent } from '@iupics-manager/models/dynamic-component';
import { Global } from '@iupics-manager/models/global-var';
import { IupicsEvent, IupicsTypeEvent } from '@iupics-manager/models/iupics-event';
import { IupicsMessage } from '@iupics-manager/models/iupics-message';
import { DynamicContainerDirective } from '@iupics-util/directives/dynamic-container.directive';
import { DateUtils } from '@iupics-util/tools/date.utils';
import { Utils } from '@iupics-util/tools/util';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import TabUiComponent from '@web-desktop/components/menu-top/components/tab-ui/tab-ui.component';
import { WorkspaceService } from '@web-desktop/components/workspace/components/workspace-ui/workspace.service';
import { UrlParamsService } from '@web-desktop/controllers/url-params.service';
import { IupicsMenuType } from '@web-desktop/models/menu-item-ui';
import { cloneDeep, has } from 'lodash';
import { MenuItem } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { of, Subscription, zip } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import shajs from 'sha.js';
import SpecificWindowUiComponent from '../specific-window-ui/specific-window-ui.component';

@Component({
  selector: 'iu-process-ui',
  templateUrl: './process-ui.component.html',
  styleUrls: ['./process-ui.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [
    DynamicContainerDirective,
    AutocompleteUiComponent,
    ButtonModule,
    ProcessLogsUiComponent,
    forwardRef(() => ModalUiComponent),
    NgClass,
    TranslateModule,
  ],
})
export default class ProcessUiComponent
  extends AbstractDynamicComponent
  implements OnInit, OnDestroy, FormModalDisplayRequester
{
  readonly #injector = inject(Injector);
  readonly #store = inject(DataStoreService);
  readonly #windowFactory = inject(WindowFactoryService);
  readonly #connectorService = inject(SecurityManagerService);
  readonly #uiCreator = inject(UICreatorService);
  readonly #processService = inject(CompiereProcessService);
  readonly #translateService = inject(TranslateService);
  readonly #socketService = inject(SocketService);
  readonly #messageManager = inject(MessageManagerService);
  readonly #progressService = inject(ProcessInProgressService);
  readonly #cacheService = inject(CacheManagerService);
  readonly #urlParamsService = inject(UrlParamsService);
  readonly #workspaceService = inject(WorkspaceService);
  private readonly renderer = inject(Renderer2);
  private readonly document = inject(DOCUMENT);

  @ViewChild('logContainer') private processLogs!: ProcessLogsUiComponent;
  @ViewChild('vcrSpecific', { read: ViewContainerRef, static: true })
  vcrSpecific!: ViewContainerRef;
  @ViewChild('specificModal', { read: ModalUiComponent, static: true })
  specificModal!: ModalUiComponent;
  @Input()
  isDisplay = true;
  @Input()
  description: string | undefined;
  @Input()
  help: string | undefined;
  @Input()
  collapsed = false;
  @Input()
  activeTab!: TabUiComponent;
  closeOnEscape = false;
  isProcessing = true;
  paramsMap: Map<string, any> = new Map<string, any>();
  private dataContainers: AbstractDataContainer[] = [];
  dataStore!: DataStore;
  //   displaySearch = false;
  searchLinkedComponent: any;
  steps: ProcessStep[] = [];
  activeIndex = 0;
  @Output()
  readonly emitter: EventEmitter<any> = new EventEmitter<any>();
  @ViewChild('fileTypesAutocomplete')
  fileTypesAutocomplete!: AbstractDataContainer;
  @ViewChild('printFormatsAutocomplete')
  printFormatsAutocomplete!: AbstractDataContainer;

  id!: string;
  name!: string;
  appName!: string;
  componentRef!: ComponentRef<any>;
  bounds!: HTMLElement;
  inBounds!: boolean;
  height!: number;
  width = 635;
  iconClass!: string;
  @Input() windowId!: number;
  @Input() parentComponent: any;
  @Input() sourceComponentData: any;
  vcrwindow!: ViewContainerRef;
  index!: number;
  processItem!: { tabId: number; label: string };
  windowType = IupicsMenuType.PROCESS;
  heightCss = '100%';
  countParams = 0;
  @Input() isModal = false;
  @Output()
  readonly closeModalEmitter = new EventEmitter<any>();

  exportDataOptions!: {
    printFormats: {
      items?: IAutocomplete[];
      isParam?: boolean;
      isMandatory?: boolean;
    };
    fileTypes: {
      items?: IAutocomplete[];
      isParam?: boolean;
      isMandatory?: boolean;
    };
  };

  channelId!: string;
  private channelSub!: Subscription;
  isReport = false;
  defaultFileType: IAutocomplete = { id: 'PDF', displayValue: 'PDF' };
  hasFormat = true;

  private progressSub!: Subscription;

  processStartTime: Date | null = null;
  processEndTime: Date | null = null;
  processElapsedLabel = signal<string>('');
  processDoneLabel = signal<string>('');
  private elapsedInterval: ReturnType<typeof setInterval> | null = null;

  private hasJasper = false;
  customFormModalBodyCss: any = undefined;
  specificWindowTitle!: string;
  displayFormUI = false;
  searchPanelValidation!: string;

  isHelpShown = false;
  isLogsOpen = false;

  //#region resizing variables
  isResizing = false;
  logsDrawerWidth = 320;

  @ViewChild('processBody') private processBodyRef!: ElementRef<HTMLElement>;

  private _resizeStartX = 0;
  private _resizeStartWidth = 0;
  private cancelResizeMousemove: () => void = () => void 0;
  private cancelResizeMouseup: () => void = () => void 0;
  private cancelResizeTouchmove: () => void = () => void 0;
  private cancelResizeTouchend: () => void = () => void 0;
  //#endregion

  protected apizUiProcess = signal<ApizUIProcess | undefined>(undefined);

  nbCol = computed(() => this.apizUiProcess()?.process.nbCol ?? 2);

  formId!: number;

  @Input()
  executeProcessWrapper: (paramsMap: ProcessRequest, channel_id: string, self: ProcessUiComponent) => void =
    this.executeProcess.bind(this);

  cmStepperEventHandler = (event: { originalEvent: MouseEvent; item: MenuItem }): void => {
    this.#urlParamsService.updateUrlParams({ id: 1000324, type: IupicsMenuType.FORM });
  };

  flatten(children: any[]): any[] {
    return children
      .map((child) => {
        if (child.children.length > 0) {
          return [...this.flatten(child.children)];
        } else {
          return [child];
        }
      })
      .flat();
  }

  ngOnInit(): void {
    if (this.activeTab) {
      this.#workspaceService.addLinkedComponentToTabMap(this.activeTab.id, this);
    }
    this.steps = [{ label: this.#translateService.instant('process.steps.noAction'), id: 0 }];
    if (this.activeTab) {
      const paramsMap: Map<string, any> = this.activeTab.processParamsMap;
      if (paramsMap) {
        this.paramsMap = paramsMap;
      }
    }
    const iupicsProcess: ApizUIProcess | undefined = cloneDeep(
      CacheManagerService.process_Params_caching.get(this.windowId)
    );
    if (iupicsProcess) {
      this.hasJasper =
        !!iupicsProcess.process.ClassName && iupicsProcess.process.ClassName.endsWith('RusReportStarter');
      this.hasFormat =
        (!iupicsProcess.process.ClassName || !iupicsProcess.process.ClassName.endsWith('RusReportStarter')) &&
        iupicsProcess.process.IsReport;
      this.isReport = this.hasJasper || this.hasFormat || iupicsProcess.process.IsReport;
      this.triggerShowProcess(iupicsProcess);
    } else {
      this.#processService.getProcess(this.windowId).subscribe({
        next: (process) => {
          this.hasJasper = process.hasJasper;
          this.hasFormat = process.hasFormat;
          this.isReport = process.isReport;
          this.countParams = process.fields?.length ?? 0;
          //load in cache
          const arr = [];
          for (const field of process.fields ?? []) {
            if (field.processId && field.processId != this.windowId) {
              this.#cacheService.loadProcessInCache(field.processId, this.#injector);
            } else if (field.formId) {
              arr.push(this.#uiCreator.getSpecificWindow(field.formId));
            }
            if (field.isRange && field.data) {
              field.data.label += ' ' + this.#translateService.instant('rangedField.from');
            } else if (field?.data?.fromRange) {
              field.data.label = this.#translateService.instant('rangedField.to');
            }
          }
          if (arr.length > 0) {
            zip(...arr).subscribe();
          }
          CacheManagerService.process_Params_caching.set(this.windowId, process);

          this.triggerShowProcess(process);
        },
        error: (error: HttpErrorResponse) => {
          if (error.status === 422) {
            const msg = this.sourceComponentData
              ? `Vous n'avez pas accès au processus ${this.sourceComponentData.data.label} [${this.sourceComponentData.data.columnName}]`
              : `Vous n'avez pas accès à ce processus`;
            throw new IupicsMessage(
              'Erreur',
              msg,
              'error',
              error.error.trace
                .map((stack: any) => `${stack.className}#${stack.methodName}:${stack.lineNumber}`)
                .join('\n')
            );
          }
        },
      });
    }
  }

  private formatDuration(ms: number): string {
    const totalSeconds = Math.floor(ms / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    if (hours > 0) {
      return `${hours}h ${minutes}m ${seconds}s`;
    }
    if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    }
    return `${seconds}s`;
  }

  private startElapsedTimer(): void {
    this.processStartTime = new Date();
    this.processEndTime = null;
    this.processElapsedLabel.set('0s');
    this.processDoneLabel.set('');
    if (this.elapsedInterval) {
      clearInterval(this.elapsedInterval);
    }
    this.elapsedInterval = setInterval(() => {
      if (this.processStartTime) {
        this.processElapsedLabel.set(this.formatDuration(Date.now() - this.processStartTime.getTime()));
      }
    }, 1000);
  }

  private stopElapsedTimer(): void {
    if (this.elapsedInterval) {
      clearInterval(this.elapsedInterval);
      this.elapsedInterval = null;
    }
    if (this.processStartTime) {
      const endTime = new Date();
      this.processEndTime = endTime;
      const duration = endTime.getTime() - this.processStartTime.getTime();
      this.processDoneLabel.set(`${endTime.toLocaleTimeString()} (${this.formatDuration(duration)})`);
    }
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    if (this.elapsedInterval) {
      clearInterval(this.elapsedInterval);
    }
    if (this.progressSub) {
      this.progressSub.unsubscribe();
    }
    this.#socketService.closeDataChannel(this.channelId);
    this.channelSub?.unsubscribe();
    if (this.activeTab) {
      this.#workspaceService.removeLinkedComponentToTabMap(this.activeTab.id);
    }
    this.cancelResizeMousemove();
    this.cancelResizeMouseup();
    this.cancelResizeTouchmove();
    this.cancelResizeTouchend();
  }

  /**
   * Demande à l'UICreator d'ajouter les champs dans notre composant
   * @param apizUiProcess
   */
  triggerShowProcess(apizUiProcess: ApizUIProcess): void {
    Global.startPerf(this, 'triggerShowProcess');
    this.apizUiProcess.set(apizUiProcess);
    this.exportDataOptions = { printFormats: {}, fileTypes: {} };
    if (apizUiProcess) {
      if (apizUiProcess.printFormats?.length) {
        this.exportDataOptions['printFormats'] = {
          items: apizUiProcess.printFormats,
          isParam: false,
          isMandatory: true,
        };
      }
      if (apizUiProcess.fileFormats && (this.hasJasper || this.isReport)) {
        if (this.defaultFileType && !apizUiProcess.fileFormats.find((ff) => ff.id === this.defaultFileType.id)) {
          this.defaultFileType = apizUiProcess.fileFormats[0];
        }
        this.exportDataOptions['fileTypes'] = {
          items: apizUiProcess.printFormats?.[0]?.fileFormats || apizUiProcess.fileFormats,
          isParam: false,
          isMandatory: true,
        };
      }
    }

    this.isReport = this.activeTab ? this.activeTab.menuType === 'Report' : this.isReport;
    this.dataStore = this.#store.newProcessData(
      this.windowId,
      this.parentComponent instanceof EditViewUiComponent
        ? (this.parentComponent as EditViewUiComponent).editTabs[0].dataStored
        : undefined
    );
    if (this.activeTab) {
      this.activeTab.isLoading = false;
    }
    this.processItem = { tabId: -1, label: apizUiProcess.process.Name };
    this.description = apizUiProcess.process.Description;
    this.help = apizUiProcess.process.Help;

    const item: DynamicComponent = {
      container: this,
      DOMParentComponent: this,
      component: 'GridUiComponent',
      cssClass: 'blade-subgrid',
      isCssOnComponent: false,
      tabId: this.windowId,
      gridPaginator: false,
      parentProcess: this,
    };
    this.#windowFactory.newEventHandler({
      type: IupicsTypeEvent.showProcess,
      item: item,
    });
    this.isProcessing = false;
    Global.endPerf(this, 'triggerShowProcess');
  }

  windowResize(event: any): void {
    if (event.height < this.height) {
      this.heightCss = '500px';
    } else {
      this.heightCss = '700px';
    }
    this.height = event.height;

    this.width = event.width;
    this.emitter.emit({ height: event.height, width: this.width });
  }

  checkMandatoryParams(): boolean {
    const fieldsMandatory = this.isConstraintMandatoryRespected();
    if (fieldsMandatory.length > 0) {
      this.#messageManager.newMessage(
        new IupicsMessage(
          this.#translateService.instant('generic.warning'),
          this.#translateService.instant('process.fillMandatoryField') + ' : [ ' + fieldsMandatory + ' ]'
        )
      );
      this.isProcessing = false;
      return false;
    }
    return true;
  }

  // eslint-disable-next-line complexity
  prepareExecuteProcess(event: Event): void {
    if (this.dataContainers.length < this.countParams) {
      return;
    }
    event.stopPropagation();
    this.clearLogs();
    const paramsMap: ProcessRequest = {
      ad_process_id: undefined,
      className: undefined,
      record_id: undefined,
      tableName: undefined,
      tables: undefined,
      params: undefined,
      ad_tab_id: undefined,
      windowCtx: this.getCurrentContext(),
    };
    this.isProcessing = true;
    if (this.checkMandatoryParams()) {
      paramsMap.params = {};
      for (const dataContainer of this.dataContainers) {
        if (dataContainer instanceof InputFileUiComponent && dataContainer.uploadedFiles.length > 0) {
          this.dataStore.data[dataContainer.data.columnName!] = dataContainer.uploadedFiles[0].src;
        }

        if (dataContainer.isDateField) {
          const date = this.dataStore.data[dataContainer.data.columnName!];
          paramsMap.params[dataContainer.data.columnName!] = date ? DateUtils.formatStr(date) : null;
        } else if (
          this.dataStore.data[dataContainer.data.columnName!] === undefined ||
          this.dataStore.data[dataContainer.data.columnName!] === null
        ) {
          paramsMap.params[dataContainer.data.columnName!] = null;
        } else {
          paramsMap.params[dataContainer.data.columnName!] = this.dataStore.data[dataContainer.data.columnName!];
        }
      }
      this.#socketService.closeDataChannel(this.channelId);
      this.channelSub?.unsubscribe();
      this.channelId = this.newChannelId();
      let recordId = null;
      let sourceTableNameTargeted = null;
      if (this.parentComponent) {
        if (
          this.parentComponent.editTabs &&
          this.parentComponent.editTabs[0] &&
          this.parentComponent.editTabs[0].data
        ) {
          // EditView Case
          paramsMap.tableName = this.parentComponent.editTabs[0].data.tableName;
          recordId = this.parentComponent.currentDataStoreKey.recordId.split(',')[1];
          sourceTableNameTargeted = this.parentComponent.editTabs[0].data.tableName;
          if (recordId !== null || recordId !== undefined) {
            if (isNaN(recordId)) {
              let colID = this.parentComponent.currentDataStoreKey.recordId.split(',')[0];
              if (!colID.endsWith('_ID')) {
                colID += '_ID';
              }
              paramsMap.record_id = this.dataStore.data[colID];
            } else {
              paramsMap.record_id = recordId;
            }
          }
        } else if (this.parentComponent.dataStore) {
          // Form Case
          recordId = this.parentComponent.dataStore.data['Record_ID'];
          paramsMap.record_id = has(recordId, 'id') ? recordId.id : recordId;
        }
      }

      paramsMap.ad_process_id = this.windowId;
      if (this.isReport) {
        paramsMap.params['FileFormat'] = this.fileTypesAutocomplete.fieldValue.id;
        if (
          this.hasFormat &&
          this.exportDataOptions &&
          (this.exportDataOptions?.printFormats?.items?.length ?? 0) > 0
        ) {
          if (this.printFormatsAutocomplete.fieldValue) {
            paramsMap.params['AD_PrintFormat_ID'] = parseInt(this.printFormatsAutocomplete.fieldValue.id, 0);
          }
        }
      }
      paramsMap.params['channel_id'] = this.channelId;
      this.#socketService.closeDataChannel(this.channelId);
      this.channelSub?.unsubscribe();
      this.channelSub = this.#socketService.openDataChannel<any>(this.channelId).subscribe((log) => {
        this.processLogs.addLog(log);
      });
      if (
        this.parentComponent &&
        this.parentComponent.container &&
        this.parentComponent.container.activeTab &&
        this.parentComponent.container.activeTab.actionID
      ) {
        // ajout des infos pour l'ouverture via notif
        paramsMap.windowCtx = Object.assign(paramsMap.windowCtx ?? {}, {
          sourceInfo: {
            sourceType: IupicsMenuType.WINDOW,
            sourceTypeId: this.parentComponent.container.activeTab.actionID,
            sourceRecordId: recordId,
            sourceTableNameTargeted: sourceTableNameTargeted,
            params: paramsMap.params,
          },
        });
      }
      this.executeProcessWrapper(paramsMap, this.channelId, this);
    }
  }

  newChannelId(): string {
    return shajs('sha256')
      .update(this.#connectorService.getIupicsUserAccount().session_id + '_' + this.windowId + '_' + Date.now())
      .digest('hex');
  }

  /**
   *
   * @param paramsMap
   * @param channel_id
   * @param self Mandatory to be able to trigger the original function from the wrapper if needed
   */
  executeProcess(paramsMap: ProcessRequest, channel_id: string, self: ProcessUiComponent): void {
    const obs = self.#processService.executeProcess(paramsMap).pipe(
      map(() => true /* pas de reponse quand 200 */),
      catchError((msg) => {
        return of(msg?.error);
      })
    );
    const obs2 = self.#cacheService.getProcess(self.windowId, self.#injector).pipe(
      map((res) => res),
      catchError(() => of(undefined))
    );
    // todo: retirer ce qui est en lien avec les steps
    self.steps = [
      { label: self.#translateService.instant('process.steps.execute'), id: 0 },
      { label: self.#translateService.instant('process.steps.inProgress'), id: 1 },
      { label: self.#translateService.instant('process.steps.executed'), id: 2 },
    ];
    self.activeIndex = 0;
    self.subscriptions.push(
      zip(obs, obs2).subscribe((values) => {
        if (self.parentComponent !== undefined) {
          (self.parentComponent as EditViewUiComponent).isProcessLaunch = true;
        }
        // ajout du process dans la file du processRunner réussi?
        if (values[0] === true) {
          if (this.apizUiProcess()?.process.shouldOpenLogs) {
            this.isLogsOpen = true;
          }
          self.activeIndex = 1;
          // récupération de l'état du process réussi?
          // TODO: retirer ce qui correspond a values[1] autant ici que dans cypress
          if (values[1]) {
            if (self.progressSub) {
              self.progressSub.unsubscribe();
            }
            self.startElapsedTimer();
            self.progressSub = self.#progressService.watchProcessInProgress().subscribe((pings) => {
              if (self.activeIndex === 1) {
                const ping = pings.find((p) => {
                  return p.Channel === channel_id;
                });
                if (ping && (ping.Status === 'finish' || ping.Status === 'cancel')) {
                  self.stopElapsedTimer();
                  self.isProcessing = false;
                  self.activeIndex = 2;
                  self.#socketService.closeDataChannel(self.channelId);
                  self.channelSub?.unsubscribe();
                  self.progressSub.unsubscribe();
                }
              }
            });
          } else {
            self.isProcessing = false;
            self.steps = [{ label: self.#translateService.instant('process.steps.noState'), id: 0 }];
            self.activeIndex = 0;
          }
        } else {
          self.isProcessing = false;
          self.steps = [{ label: self.#translateService.instant('process.steps.notExecuted'), id: 0 }];
          if (values[0]?.error && values[0].error.length > 0) {
            self.processLogs.addLog(values[0].error);
          }
          self.activeIndex = 0;
        }
      })
    );
  }

  isConstraintMandatoryRespected(): string[] {
    const isOk = [];
    if (this.dataContainers) {
      let i = 0;
      while (isOk && i < this.dataContainers.length) {
        if (
          !this.dataContainers[i].isAccordion &&
          this.dataContainers[i].displayCss !== 'none' &&
          this.dataContainers[i].isMandatory &&
          (this.dataContainers[i].fieldValue === undefined ||
            this.dataContainers[i].fieldValue === null ||
            this.dataContainers[i].fieldValue === '')
        ) {
          isOk.push(this.dataContainers[i].label);
        }
        i++;
      }
    }
    return isOk;
  }

  updateContainerZone(n: number): void {}

  onChildUpdate(event: IupicsEvent): void {}

  onSiblingUpdate(event: IupicsEvent): void {}

  onRemoveComponent(event: IupicsEvent): void {}

  onPrintFormatChange(event: PrintFormatAutocomplete): void {
    if (event.fileFormats?.length) {
      this.fileTypesAutocomplete.data.items = event.fileFormats;
    } else {
      this.fileTypesAutocomplete.data.items = this.apizUiProcess()?.fileFormats;
    }
  }

  addDataContainerToEditTab(dataContainer: AbstractDataContainer): void {
    // binding it to get the parent context
    if (!this.parentComponent) {
      dataContainer.notifyUrlChange = this.notifyUrlChange.bind(this);
    }
    this.dataContainers.push(dataContainer);
  }

  notifyUrlChange(): void {
    // update url to fit fields current state
    const paramsMap: Map<string, any> = new Map<string, any>();
    for (const child of this.DOMChildrenComponent) {
      for (const child2 of child.DOMChildrenComponent) {
        if (
          (child2 as AbstractDataContainer).fieldValue &&
          (!(child2 as AbstractDataContainer).data || (child2 as AbstractDataContainer).data.fieldType !== 'password')
        ) {
          if (child2 instanceof CalendarUiComponent) {
            const value = (child2 as AbstractDataContainer).fieldValue as Date;
            paramsMap.set(child2.data.columnName!, value.getTime());
          } else {
            paramsMap.set(child2.data.columnName!, (child2 as AbstractDataContainer).fieldValue);
          }
        }
      }
    }
    Utils.onTabChange(this.activeTab, undefined, paramsMap);
  }

  clearLogs(): void {
    this.processLogs.clearLogs();
  }

  showCMChecker(item: ProcessStep): boolean {
    if (item.id === 1) {
      return true;
    }
    return false;
  }

  getWindowContext(): any {
    return this.getCurrentContext();
  }

  /**
   * récupération du contexte complet du composant
   * @param dataStore nouveau datastore à prendre en compte
   */
  getCurrentContext(dataStore?: DataStore): any {
    let specificParent;
    let editViewParent;
    if (this.parentComponent && this.parentComponent.editTabs && this.parentComponent.editTabs[0]) {
      editViewParent = this.parentComponent;
    } else if (this.parentComponent instanceof SpecificWindowUiComponent) {
      specificParent = this.parentComponent;
    }
    /*merge avec le contexte du parent */
    if (specificParent) {
      const specificParentContext = specificParent.getCurrentContext();
      return EditViewUtils.mergeCurrentDataDeepCopy(
        cloneDeep(dataStore ? dataStore : this.dataStore.data),
        specificParentContext
      );
    } else {
      return EditViewUtils.getCurrentContext(
        editViewParent,
        dataStore ? dataStore : this.dataStore,
        this.#connectorService.getIupicsUserContext()
      );
    }
  }

  /*
   * emailEditor: { display: { key: 'displayEmailEditor', value: displayEmailEditor }}
   * joinFilesPanel: { display: { key: 'displayJoinFilesPanel', value: displayJoinFilesPanel }}
   * processModal: { display: { key: 'displayProcessUI', value: displayProcessUI }, id: { key: 'processId', value: processId } }
   * formModal: { display: { key: 'displayFormUI', value: displayFormUI }, id: { key: 'formId', value: formId } }
   * sourceComponent : déterminer si autocomplete ou button
   */
  updateModalDisplay(request: ModalDisplayRequest): void {
    if (!isFormModalDisplayRequest(request)) {
      return;
    }
    const { display, id } = request;
    this.customFormModalBodyCss =
      display.key && display.key === 'displayFormUI'
        ? { 'iu-modal-body': { overflow: 'auto', height: 'calc(100% - 25px)' } }
        : undefined;
    this[display.key] = display.value;
    if (id && display.value === true) {
      this[id.key] = id.value;
      if (id.key === 'formId') {
        this.createSpecificWindow(id.value, display.sourceComponent);
      }
    } else {
      this.customFormModalBodyCss = undefined;
    }
  }

  createSpecificWindow(formId: number, sourceComponent: any): void {
    this.subscriptions.push(
      this.#uiCreator.getSpecificWindow(formId).subscribe((specificWindow) => {
        let component: Type<SpecificWindowUiComponent> | undefined;
        if (
          specificWindow.angularClass &&
          specificWindow.angularClass.length > 0 &&
          specificWindow.angularClass !== 'default'
        ) {
          component = CacheManagerService.iupics_specific_window.get(specificWindow.angularClass);
        }
        if (!component) {
          component = CacheManagerService.iupics_specific_window.get('default');
        }
        if (!component) {
          return;
        }
        this.vcrSpecific.clear();
        const componentRef = this.vcrSpecific.createComponent(component);
        this.specificWindowTitle = specificWindow.name;
        (componentRef.instance as SpecificWindowUiComponent).name = specificWindow.name;
        (componentRef.instance as SpecificWindowUiComponent).title = specificWindow.title ?? '';
        (componentRef.instance as SpecificWindowUiComponent).description = specificWindow.description;
        (componentRef.instance as SpecificWindowUiComponent).help = specificWindow.help;
        (componentRef.instance as SpecificWindowUiComponent).componentRef = componentRef;
        (componentRef.instance as SpecificWindowUiComponent).isModal = true;
        (componentRef.instance as SpecificWindowUiComponent).formId = formId;
        (componentRef.instance as SpecificWindowUiComponent).vcrwindow = this.vcrSpecific;
        (componentRef.instance as SpecificWindowUiComponent).parentComponent = this;
        (componentRef.instance as SpecificWindowUiComponent).sourceModal = this.specificModal;
        (componentRef.instance as SpecificWindowUiComponent).index = this.vcrSpecific.length - 1;
        (componentRef.instance as SpecificWindowUiComponent).sourceComponentData =
          sourceComponent && sourceComponent.itemData ? sourceComponent.itemData : sourceComponent;
        (componentRef.instance as SpecificWindowUiComponent).sourceComponent =
          sourceComponent; /**dupliqué si pas d'itemdata mais trop de refactoring sur scout */
        (componentRef.instance as SpecificWindowUiComponent).closeModalEmitter
          .pipe(takeUntilDestroyed((componentRef.instance as SpecificWindowUiComponent).destroyRef))
          .subscribe(() => this.updateModalDisplay(ModalDisplayRequest.Form(false)));
        this.componentRefs.push(componentRef);
      })
    );
  }

  //#region resizing functions
  onResizeStart(event: MouseEvent | TouchEvent): void {
    if (!this.isLogsOpen) {
      return;
    }
    event.preventDefault();
    this.isResizing = true;
    this._resizeStartX = this.getResizeClientX(event);
    this._resizeStartWidth = this.logsDrawerWidth;
    this.cancelResizeMousemove = this.renderer.listen(this.document, 'mousemove', (e: MouseEvent) =>
      this.onResizeMove(e)
    );
    this.cancelResizeMouseup = this.renderer.listen(this.document, 'mouseup', () => this.onResizeEnd());
    this.cancelResizeTouchmove = this.renderer.listen(this.document, 'touchmove', (e: TouchEvent) =>
      this.onResizeMove(e)
    );
    this.cancelResizeTouchend = this.renderer.listen(this.document, 'touchend', () => this.onResizeEnd());
  }

  private getResizeClientX(event: MouseEvent | TouchEvent): number {
    return event instanceof TouchEvent ? event.touches[0].clientX : event.clientX;
  }

  onResizeMove(event: MouseEvent | TouchEvent): void {
    if (!this.isResizing) {
      return;
    }
    const delta = this._resizeStartX - this.getResizeClientX(event);
    const bodyWidth = this.processBodyRef?.nativeElement?.offsetWidth ?? 1200;
    this.logsDrawerWidth = Math.max(200, Math.min(this._resizeStartWidth + delta, bodyWidth - 200));
  }

  onResizeEnd(): void {
    this.isResizing = false;
    this.cancelResizeMousemove();
    this.cancelResizeMouseup();
    this.cancelResizeTouchmove();
    this.cancelResizeTouchend();
  }
  //#endregion
}

export type ProcessStep = {
  label: string;
  id: number;
};
